import { useListState } from "../../lib/listState";
import { useQuery } from "@tanstack/react-query";
import { Database, FileWarning, Layers3, MapPinned, Search, Tags } from "lucide-react";
import { api } from "../../lib/api";
import type {
  TechOpsMasterDataImportRow,
  TechOpsOrganizationMasterDataRow
} from "../../lib/types";
import { formatDateTime, formatNumber, labelFromCode, statusTone } from "../shared/format";
import { RouteError, RouteLoading } from "../shared/PageState";

function StatTile({ label, value, detail }: { label: string; value: number; detail: string }) {
  return (
    <div className="pa-stat-tile">
      <small>{label}</small>
      <strong>{formatNumber(value)}</strong>
      <span>{detail}</span>
    </div>
  );
}

function MasterDataRows({ organizations }: { organizations: TechOpsOrganizationMasterDataRow[] }) {
  if (organizations.length === 0) {
    return (
      <tr>
        <td colSpan={8} className="pa-empty-cell">No organization master data matches the current search.</td>
      </tr>
    );
  }
  return organizations.map((organization) => (
    <tr key={organization.organization_id}>
      <td>
        <strong>{organization.organization_name}</strong>
        <small>{organization.organization_type}</small>
      </td>
      <td><span className={`pa-badge ${statusTone(organization.status)}`}>{organization.status}</span></td>
      <td>{formatNumber(organization.location_group_count)}</td>
      <td>{formatNumber(organization.location_count)}</td>
      <td>{formatNumber(organization.catalogue_item_count)}</td>
      <td>{formatNumber(organization.item_mapping_count)}</td>
      <td>{formatNumber(organization.active_rate_card_count)}</td>
      <td>
        <strong>{formatNumber(organization.import_count)}</strong>
        <small>{formatNumber(organization.imports_needing_review_count)} need review</small>
      </td>
    </tr>
  ));
}

function ImportRows({ imports }: { imports: TechOpsMasterDataImportRow[] }) {
  if (imports.length === 0) {
    return (
      <tr>
        <td colSpan={6} className="pa-empty-cell">No recent master-data imports.</td>
      </tr>
    );
  }
  return imports.map((item) => (
    <tr key={item.import_id}>
      <td>
        <strong>{item.file_name}</strong>
        <small>{item.organization_name}</small>
      </td>
      <td>{labelFromCode(item.import_type)}</td>
      <td><span className={`pa-badge ${statusTone(item.status)}`}>{item.status}</span></td>
      <td>{formatNumber(item.processed_rows)} / {formatNumber(item.total_rows)}</td>
      <td>{formatNumber(item.error_rows)}</td>
      <td>{formatDateTime(item.updated_at)}</td>
    </tr>
  ));
}

export default function MasterDataPage() {
  const [search, setSearch] = useListState("master-data.search", "");
  const masterDataQuery = useQuery({
    queryKey: ["tech-ops-master-data", search],
    queryFn: () => api.getMasterData({ search })
  });

  if (masterDataQuery.isLoading) {
    return <RouteLoading />;
  }

  if (masterDataQuery.isError || !masterDataQuery.data) {
    return (
      <RouteError
        title="Master data unavailable"
        detail="The master-data view could not be loaded from the Coprint backend."
        onRetry={() => masterDataQuery.refetch()}
      />
    );
  }

  const { summary, organizations, recent_imports: recentImports, generated_at: generatedAt } = masterDataQuery.data;

  return (
    <div className="pa-page-stack">
      <div className="pa-title-row">
        <div>
          <p className="pa-eyebrow">Master Data</p>
          <h1>Master data health</h1>
          <p>Global items, organization catalogue readiness, locations and import status.</p>
        </div>
        <span className="pa-refresh-chip">Updated {formatDateTime(generatedAt)}</span>
      </div>

      <section className="pa-stat-grid six">
        <StatTile label="Coprint Items" value={summary.brandwise_items} detail="Global catalogue" />
        <StatTile label="Locations" value={summary.organization_locations} detail="Active org locations" />
        <StatTile label="Catalogue" value={summary.organization_catalogue_items} detail="Org item rows" />
        <StatTile label="Mappings" value={summary.item_mappings} detail="Confirmed item links" />
        <StatTile label="Rate Cards" value={summary.active_rate_cards} detail="Active commercial data" />
        <StatTile label="Failed Imports" value={summary.failed_imports} detail="Needs operator review" />
      </section>

      <section className="pa-panel">
        <div className="pa-toolbar">
          <label className="pa-search-field">
            <Search size={16} />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search organizations" />
          </label>
          <div className="pa-toolbar-icons">
            <span><Database size={15} /> Global</span>
            <span><MapPinned size={15} /> Locations</span>
            <span><Tags size={15} /> Catalogue</span>
            <span><Layers3 size={15} /> Mappings</span>
          </div>
        </div>
        <div className="pa-table-scroll">
          <table className="pa-table">
            <thead>
              <tr>
                <th>Organization</th>
                <th>Status</th>
                <th>Groups</th>
                <th>Locations</th>
                <th>Catalogue</th>
                <th>Mappings</th>
                <th>Rate Cards</th>
                <th>Imports</th>
              </tr>
            </thead>
            <tbody>
              <MasterDataRows organizations={organizations} />
            </tbody>
          </table>
        </div>
      </section>

      <section className="pa-panel">
        <div className="pa-panel-header">
          <h2><FileWarning size={16} /> Recent Imports</h2>
        </div>
        <div className="pa-table-scroll">
          <table className="pa-table">
            <thead>
              <tr>
                <th>File</th>
                <th>Type</th>
                <th>Status</th>
                <th>Processed</th>
                <th>Errors</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              <ImportRows imports={recentImports} />
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
